import { button } from "./button.ts";
import { cx, h, type Child } from "./dom.ts";
import { icon, type IconNode } from "./icons.ts";
import { ui } from "./strings.ts";
import { TONE_ICONS, type Tone } from "./toast.ts";

/**
 * Os estados de retorno de toda tela (ADR 0024): carregando, vazio, erro e aviso. Cada um tem o texto
 * que diz o que aconteceu e, quando dá, o próximo passo (tentar de novo, criar o primeiro, ajustar o
 * filtro). Nenhum depende só da cor: há sempre texto e ícone.
 */

export interface AlertOptions {
  tone?: Tone | undefined;
  title?: string | undefined;
  message: Child;
  /** Botão ou link do próximo passo, à direita. */
  action?: Node | undefined;
  /** Anunciar ao aparecer (`role="alert"` no erro, `role="status"` no resto). */
  live?: boolean | undefined;
  class?: string | undefined;
}

/** Aviso dentro da página (fica até a situação mudar). `alertBox`, e não `alert`, para não esconder o `window.alert`. */
export function alertBox(options: AlertOptions): HTMLDivElement {
  const tone = options.tone ?? "info";
  const role = options.live === false ? undefined : tone === "destructive" ? "alert" : options.live ? "status" : undefined;
  return h(
    "div",
    { class: cx("alert", tone !== "info" && `alert-${tone}`, options.class), role },
    icon(TONE_ICONS[tone]),
    h("div", { class: "alert-body" }, options.title ? h("p", { class: "alert-title" }, options.title) : null, h("div", {}, options.message)),
    options.action ?? null,
  );
}

export interface EmptyStateOptions {
  icon?: IconNode | undefined;
  title: string;
  description?: string | undefined;
  /** O próximo passo: limpar o filtro, criar o primeiro, importar. */
  action?: Node | undefined;
  class?: string | undefined;
}

/** Nada para mostrar (lista vazia, busca sem resultado). */
export function emptyState(options: EmptyStateOptions): HTMLDivElement {
  return h(
    "div",
    { class: cx("empty-state", options.class) },
    options.icon ? icon(options.icon) : null,
    h("p", { class: "empty-state-title" }, options.title),
    options.description ? h("p", { class: "empty-state-description" }, options.description) : null,
    options.action ? h("div", { class: "mt-2" }, options.action) : null,
  );
}

/** Falha ao carregar: a mensagem e "Tentar de novo". */
export function errorState(message: string, onRetry?: () => void, retryLabel = ui("retry")): HTMLDivElement {
  return alertBox({ tone: "destructive", message, action: onRetry ? button({ variant: "outline", size: "sm", onClick: () => onRetry() }, retryLabel) : undefined, class: "items-center" });
}

/** Carregando: o giro e o texto, num `role="status"` (lido quando a pessoa chega nele ou quando o texto muda). */
export function loadingState(text = ui("loading"), extra?: string): HTMLDivElement {
  return h("div", { class: cx("loading-state loading", extra), role: "status" }, h("span", { class: "spinner", "aria-hidden": "true" }), h("span", {}, text));
}

/** Bloco de espera no formato do conteúdo que vem (cartão, linha de tabela, texto). */
export const skeleton = (cls = "h-4 w-full"): HTMLDivElement => h("div", { class: cx("skeleton", cls), "aria-hidden": "true" });

export const spinner = (label?: string): HTMLSpanElement => h("span", { class: "spinner", role: label ? "status" : undefined, "aria-label": label, "aria-hidden": label ? undefined : "true" });

/** Barra de progresso; `value` de 0 a 1, ou `null` para indeterminado. */
export function progress(label: string, value: number | null = null): HTMLDivElement {
  const bar = h("div", { class: "progress", role: "progressbar", "aria-label": label, "aria-valuemin": 0, "aria-valuemax": 100 }, h("span"));
  setProgress(bar, value);
  return bar;
}

export function setProgress(bar: HTMLElement, value: number | null): void {
  if (value === null) {
    bar.dataset["indeterminate"] = "";
    bar.removeAttribute("aria-valuenow");
    return;
  }
  const v = Math.min(1, Math.max(0, value));
  delete bar.dataset["indeterminate"];
  bar.setAttribute("aria-valuenow", String(Math.round(v * 100)));
  bar.style.setProperty("--value", String(v));
}
